import { NavMarker } from 'react-rhumb';
import { Table, SortTH } from 'foundations-components/transitional/components/Table';
import FormattedMessage from 'I18n/components/FormattedMessage';
import ApiError from '../../../../components/Errors/ApiError';
import ACTableSkeleton from 'academy-common-lib/components/loading/ACTableSkeleton';
import { DEFAULT_PAGE_SIZE } from '../../constants/pagination';
import UserAvatarLink from '../common/UserAvatarLink';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import { useSyncTableStateWithUrl, useTeamMembersTableStore } from '../../store/myTeamTableStateStore';
import MyTeamTablePagination from '../common/TablePagination';
import EmptyTable from '../common/EmptyTable';
import { useTableSorting } from '../../hooks/tableHooks';
import { Flex } from 'foundations-components/components/Flex';
import { useUserInfo } from '../../../accounts/accountsQueries';
import { useCurrentUserCertificationsByStatus, useTeamMembersCertificationAggregationsByStatus } from '../../myTeamQueries';
import { getOffsetPosition, getPaginatedPosition } from '../../utils/paginationUtils';
import { EXPIRED_CERTIFICATIONS_BY_TEAM_PATH, IN_PROGRESS_CERTIFICATIONS_BY_TEAM_PATH } from '../../constants/routes';
import { ACTIVE_CERTIFICATIONS_BY_TEAM_PATH } from '../../constants/routes';
import { useIsAnyFilterSet } from '../../hooks/filtersHooks';
import DrilldownLink from '../common/DrilldownLink';
import { useCallback } from 'react';
import ExpiringSoonHelpIcon from '../common/ExpiringSoonHelpIcon';
import { useTheme } from 'foundations-theming/utils';
import { View } from 'foundations-components/components/View';
import { ACTableRowNoInlineRadius } from 'academy-common-lib/components/table/SharedTableComponents';
import CurrentUserCell from './CurrentUserCell';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PAGE_SIZE_WITH_CURRENT_USER = DEFAULT_PAGE_SIZE;
const PAGE_SIZE_WITHOUT_CURRENT_USER = DEFAULT_PAGE_SIZE - 1;
const CurrentUserCertificationsRow = ({
  children,
  'data-test-id': dataTestId
}) => /*#__PURE__*/_jsx(View, {
  as: "tr",
  borderBlockEndColor: "border-core-subtle",
  borderBlockEndStyle: "solid",
  borderBlockEndWidth: "300",
  "data-test-id": dataTestId,
  children: children
});
export const TeamMembersTable = () => {
  const {
    userInfo,
    userInfoLoading,
    userInfoError
  } = useUserInfo();
  const teamMemberTableStore = useTeamMembersTableStore();
  const theme = useTheme();
  useSyncTableStateWithUrl(teamMemberTableStore);
  const {
    state: {
      teams,
      users,
      categories,
      tools,
      certificationStatuses,
      sortBy,
      sortOrder,
      search,
      page,
      isUserPinned
    },
    actions: {
      resetTableState,
      setSortBy,
      setSortOrder,
      setPage
    }
  } = teamMemberTableStore;
  const {
    handleSortChange,
    getSortDirectionForColumn
  } = useTableSorting({
    sortBy,
    sortOrder,
    setSortBy,
    setSortOrder
  });
  const {
    currentUserCertifications,
    currentUserCertificationsLoading,
    currentUserCertificationsError
  } = useCurrentUserCertificationsByStatus({
    currentUserId: userInfo === null || userInfo === void 0 ? void 0 : userInfo.user.id,
    categories,
    tools
  });
  const offsetPosition = getOffsetPosition(page, PAGE_SIZE_WITHOUT_CURRENT_USER);
  const {
    teamMembersData,
    teamMembersDataLoading,
    teamMembersDataError
  } = useTeamMembersCertificationAggregationsByStatus({
    pageLength: isUserPinned ? PAGE_SIZE_WITHOUT_CURRENT_USER : PAGE_SIZE_WITH_CURRENT_USER,
    offset: {
      offsetPosition
    },
    teamIds: teams,
    userIds: users,
    categories,
    tools,
    certificationStatuses,
    sortOrder,
    sortField: sortBy,
    teamMemberNameFilter: search,
    excludeCurrentUser: !!isUserPinned
  });
  const buildDrilldownLink = useCallback((path, userId) => {
    const drilldownQueryString = new URLSearchParams(Object.assign({}, categories.length && {
      categories: categories.join(',')
    }, tools.length && {
      tools: tools.join(',')
    }, {
      users: userId.toString()
    })).toString();
    return `${path}${drilldownQueryString ? `?${drilldownQueryString}` : ''}`;
  }, [categories, tools]);
  const isAnyLoading = teamMembersDataLoading || userInfoLoading || currentUserCertificationsLoading;
  const isAnyError = teamMembersDataError || userInfoError || currentUserCertificationsError;
  const {
    isDesktop
  } = useMediaQuery();
  const isAnyFilterSet = useIsAnyFilterSet(teamMemberTableStore, ['teams', 'users', 'categories', 'tools', 'certificationStatuses', 'search']);

  // This table will never be empty if there are no filters set because the current user will be shown
  // however we do want to show the empty state if the user has applied filters that result in no data
  const isEmpty = !isAnyLoading && (teamMembersData === null || teamMembersData === void 0 ? void 0 : teamMembersData.results.length) === 0 && isAnyFilterSet;
  if (isEmpty) {
    return /*#__PURE__*/_jsx(EmptyTable, {
      navMarkerName: "TEAM_MEMBERS_EMPTY",
      onResetClick: resetTableState
    });
  }
  if (isAnyError) {
    return /*#__PURE__*/_jsx(NavMarker, {
      name: "TEAM_MEMBERS_API_ERROR",
      children: /*#__PURE__*/_jsx(ApiError, {
        error: {
          status: 500
        }
      })
    });
  }
  return isAnyLoading ? /*#__PURE__*/_jsx(ACTableSkeleton, {
    columns: 7,
    condensed: true,
    headerRows: 2,
    rows: PAGE_SIZE_WITH_CURRENT_USER
  }) : teamMembersData && /*#__PURE__*/_jsxs(NavMarker, {
    name: "TEAM_MEMBERS_LOADED",
    children: [/*#__PURE__*/_jsxs(Table, {
      condensed: true,
      "data-test-id": "team-members-table",
      hover: true,
      responsive: true,
      children: [/*#__PURE__*/_jsxs("colgroup", {
        children: [/*#__PURE__*/_jsx("col", {
          style: {
            width: '40%',
            borderInlineEnd: `${theme.borderWidth['100']} solid ${theme.color['border-core-subtle']}`
          }
        }), /*#__PURE__*/_jsx("col", {
          style: {
            width: '15%'
          }
        }), /*#__PURE__*/_jsx("col", {
          style: {
            width: '15%'
          }
        }), /*#__PURE__*/_jsx("col", {
          style: {
            width: '15%'
          }
        }), /*#__PURE__*/_jsx("col", {
          style: {
            width: '15%'
          }
        })]
      }), /*#__PURE__*/_jsxs("thead", {
        children: [/*#__PURE__*/_jsxs("tr", {
          children: [/*#__PURE__*/_jsx("th", {}), /*#__PURE__*/_jsx("th", {
            colSpan: 4,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.myTeam.tableColumns.certifications"
            })
          })]
        }), /*#__PURE__*/_jsxs(ACTableRowNoInlineRadius, {
          children: [/*#__PURE__*/_jsx(SortTH, {
            onClick: () => handleSortChange('NAME'),
            sort: getSortDirectionForColumn('NAME'),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.myTeam.tableColumns.name"
            })
          }), /*#__PURE__*/_jsx(SortTH, {
            onClick: () => handleSortChange('ACTIVE'),
            sort: getSortDirectionForColumn('ACTIVE'),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.myTeam.tableColumns.active"
            })
          }), /*#__PURE__*/_jsx(SortTH, {
            onClick: () => handleSortChange('EXPIRING_SOON'),
            sort: getSortDirectionForColumn('EXPIRING_SOON'),
            children: /*#__PURE__*/_jsxs(Flex, {
              gap: "100",
              children: [/*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.myTeam.tableColumns.expiringSoon"
              }), /*#__PURE__*/_jsx(ExpiringSoonHelpIcon, {})]
            })
          }), /*#__PURE__*/_jsx(SortTH, {
            onClick: () => handleSortChange('EXPIRED'),
            sort: getSortDirectionForColumn('EXPIRED'),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.myTeam.tableColumns.expired"
            })
          }), /*#__PURE__*/_jsx(SortTH, {
            onClick: () => handleSortChange('IN_PROGRESS'),
            sort: getSortDirectionForColumn('IN_PROGRESS'),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.myTeam.tableColumns.inProgress"
            })
          })]
        })]
      }), /*#__PURE__*/_jsxs("tbody", {
        children: [currentUserCertifications && isUserPinned && /*#__PURE__*/_jsxs(CurrentUserCertificationsRow, {
          "data-test-id": "current-user-data-row",
          children: [/*#__PURE__*/_jsx(CurrentUserCell, {
            children: /*#__PURE__*/_jsx(UserAvatarLink, {
              displayAvatar: isDesktop,
              position: 0,
              userEmail: currentUserCertifications.userEmail,
              userId: currentUserCertifications.userId,
              userName: currentUserCertifications.userName
            })
          }), /*#__PURE__*/_jsx("td", {
            children: /*#__PURE__*/_jsx(DrilldownLink, {
              aggregationTotal: currentUserCertifications.certificationTotals.active,
              to: buildDrilldownLink(ACTIVE_CERTIFICATIONS_BY_TEAM_PATH, currentUserCertifications.userId)
            })
          }), /*#__PURE__*/_jsx("td", {
            children: /*#__PURE__*/_jsx(DrilldownLink, {
              aggregationTotal: currentUserCertifications.certificationTotals.expiringSoon,
              to: buildDrilldownLink(ACTIVE_CERTIFICATIONS_BY_TEAM_PATH, currentUserCertifications.userId)
            })
          }), /*#__PURE__*/_jsx("td", {
            children: /*#__PURE__*/_jsx(DrilldownLink, {
              aggregationTotal: currentUserCertifications.certificationTotals.expired,
              to: buildDrilldownLink(EXPIRED_CERTIFICATIONS_BY_TEAM_PATH, currentUserCertifications.userId)
            })
          }), /*#__PURE__*/_jsx("td", {
            children: /*#__PURE__*/_jsx(DrilldownLink, {
              aggregationTotal: currentUserCertifications.certificationTotals.inProgress,
              to: buildDrilldownLink(IN_PROGRESS_CERTIFICATIONS_BY_TEAM_PATH, currentUserCertifications.userId)
            })
          })]
        }, currentUserCertifications.userId), teamMembersData.results.map(({
          userEmail,
          userId,
          userName,
          certificationTotals: {
            active,
            expiringSoon,
            expired,
            inProgress
          }
        }, index) => {
          const userAvatarLink = /*#__PURE__*/_jsx(UserAvatarLink, {
            displayAvatar: isDesktop,
            position: getPaginatedPosition(offsetPosition, index),
            userEmail: userEmail,
            userId: userId,
            userName: userName
          });
          const isCurrentUser = userId === (userInfo === null || userInfo === void 0 ? void 0 : userInfo.user.id);
          return /*#__PURE__*/_jsxs("tr", {
            "data-test-id": "team-member-data-row",
            children: [isCurrentUser ? /*#__PURE__*/_jsx(CurrentUserCell, {
              children: userAvatarLink
            }) : /*#__PURE__*/_jsx("td", {
              children: userAvatarLink
            }), /*#__PURE__*/_jsx("td", {
              "data-test-id": "active-count",
              children: /*#__PURE__*/_jsx(DrilldownLink, {
                aggregationTotal: active,
                to: buildDrilldownLink(ACTIVE_CERTIFICATIONS_BY_TEAM_PATH, userId)
              })
            }), /*#__PURE__*/_jsx("td", {
              "data-test-id": "expiring-soon-count",
              children: /*#__PURE__*/_jsx(DrilldownLink, {
                aggregationTotal: expiringSoon,
                to: buildDrilldownLink(ACTIVE_CERTIFICATIONS_BY_TEAM_PATH, userId)
              })
            }), /*#__PURE__*/_jsx("td", {
              "data-test-id": "expired-count",
              children: /*#__PURE__*/_jsx(DrilldownLink, {
                aggregationTotal: expired,
                to: buildDrilldownLink(EXPIRED_CERTIFICATIONS_BY_TEAM_PATH, userId)
              })
            }), /*#__PURE__*/_jsx("td", {
              "data-test-id": "in-progress-count",
              children: /*#__PURE__*/_jsx(DrilldownLink, {
                aggregationTotal: inProgress,
                to: buildDrilldownLink(IN_PROGRESS_CERTIFICATIONS_BY_TEAM_PATH, userId)
              })
            })]
          }, userId);
        })]
      })]
    }), /*#__PURE__*/_jsx(MyTeamTablePagination, {
      onPageChange: setPage,
      page: page,
      pageSize: PAGE_SIZE_WITHOUT_CURRENT_USER,
      totalResults: teamMembersData.total
    })]
  });
};
export default TeamMembersTable;